/**
 * The per-product EDITION choice — Data Manager's Basic | Automation, and
 * Prospect Finder's Basic | Automation — used by Create Tenant (inside the
 * product row) and Edit Tenant (ProductEditionSection).
 *
 * EVERYTHING SHOWN COMES FROM THE SERVER'S ROW. Codes, labels, what each
 * edition includes, its price and the AI / downgrade notes are all on the
 * presentation row (`commercial_presentation._row`). Nothing here names an
 * edition, prices one or decides what it means, so a relabel or a new
 * edition needs no edit to this file.
 *
 * Prices: the base edition IS the product's own line; an edition with a
 * `priced_item` is an uplift charged on top, so it reads "+ USD x". An
 * unpriced edition reads the server's words ("Not in the default price
 * book") and is never shown as 0.
 */
import React from 'react';

function priceText(price) {
  if (!price) return null;
  if (!price.priced) return price.display;
  const amount = `${price.currency} ${Number(price.amount).toFixed(2)}`;
  return `${price.kind === 'uplift' ? '+ ' : ''}${amount} ${price.billing_unit_label}`;
}

export default function EditionPicker({
  code, row, value, onChange, current = null, disabled = false,
}) {
  const editions = row?.editions || [];
  if (editions.length === 0) return null;
  const chosen = value ?? current ?? row.default_edition;
  const rank = (e) => editions.findIndex((ed) => ed.code === e);
  const chosenEd = editions.find((ed) => ed.code === chosen);
  const notes = row.edition_notes || {};
  // A DOWNGRADE is only a downgrade of something the tenant already has, so
  // the warning appears in Edit Tenant (where `current` is known), never on a
  // tenant that does not exist yet.
  const downgrading = current != null && rank(chosen) < rank(current);
  const capable = rank(chosen) > 0;

  return (
    <div className="mt-2 pl-6 space-y-1" data-testid={`editions-${code}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground">Edition</span>
        <div className="flex gap-1" role="radiogroup" aria-label="Edition">
          {editions.map((ed) => {
            const on = chosen === ed.code;
            return (
              <button key={ed.code} type="button" role="radio" aria-checked={on}
                      disabled={disabled}
                      data-testid={`edition-${code}-${ed.code}`}
                      onClick={() => onChange(ed.code)}
                      className={`px-2.5 py-1 rounded-full text-xs border transition ${
                        on ? 'bg-primary text-primary-foreground border-primary'
                           : 'bg-card text-muted-foreground border-input'}`}>
                {ed.label}
                {ed.price && (
                  <span className="ml-1 opacity-80" data-testid={`edition-price-${code}-${ed.code}`}>
                    · {priceText(ed.price)}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
      {chosenEd?.description && (
        <p className="text-xs text-foreground" data-testid={`edition-description-${code}`}>
          {chosenEd.description}
        </p>
      )}
      {capable && notes.ai && (
        <p className="text-xs text-muted-foreground" data-testid={`edition-ai-note-${code}`}>
          {notes.ai}
        </p>
      )}
      {downgrading && notes.downgrade && (
        <p className="text-xs text-warning-text" role="alert"
           data-testid={`edition-downgrade-${code}`}>
          {notes.downgrade}
        </p>
      )}
    </div>
  );
}
